import {
    PanelTransaction,
    PanelTransactionsApiResult,
    PanelTransactionsQuery,
    PanelTransactionScope,
    PanelTransactionsSummary,
    PanelTransactionsSummaryBucket,
} from './panel_transactions.types';
import { EMPTY_SUMMARY } from './panel_transactions.constants';

// Mismo host y llave que el resto de `commercial_panel/*`.
const BASE_URL = process.env.REACT_APP_API_PAYMENT_METHODS ?? '';
const API_KEY = process.env.REACT_APP_XAPIKEY_TRANSACTION_AUTHORIZE ?? '';

interface TransactionsEnvelope {
    success?: boolean;
    errors?: { code?: number; message?: string; json_message?: unknown };
    data?: {
        items?: PanelTransaction[];
        total?: number;
        page?: number;
        page_size?: number;
        scope?: PanelTransactionScope;
        history_available_from?: string;
        summary?: Partial<PanelTransactionsSummary>;
    };
}

function headers(token: string): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-api-key': API_KEY,
    };
}

const FALLBACK_ERROR = 'Ocurrió un error al cargar las transacciones';

const toBucket = (raw: PanelTransactionsSummaryBucket | undefined): PanelTransactionsSummaryBucket => ({
    count: Number(raw?.count) || 0,
    amount: Number(raw?.amount) || 0,
});

/**
 * Los tres cubos siempre existen. Un envelope viejo o incompleto no puede
 * dejar a la UI leyendo `undefined` e inventando cifras de la página.
 */
const normalizeSummary = (
    raw: Partial<PanelTransactionsSummary> | undefined,
): PanelTransactionsSummary => ({
    in_process: toBucket(raw?.in_process ?? EMPTY_SUMMARY.in_process),
    successful: toBucket(raw?.successful ?? EMPTY_SUMMARY.successful),
    failed: toBucket(raw?.failed ?? EMPTY_SUMMARY.failed),
});

/**
 * GET `commercial_panel/transactions` — listado paginado de transacciones del
 * panel interno.
 *
 * El comercial responsable NO viaja como parámetro: el backend lo toma del JWT
 * y con eso recorta las filas. `customerEmail` es el filtro de correo del
 * listado: con valor acota a ese cliente (Estado A), vacío pide la cartera
 * (Estado B). No cambia el alcance.
 *
 * `transactionId` es un tercer modo: busca una transacción por su número y
 * reemplaza a los demás filtros. `scope` viaja igual, porque el alcance no es un
 * filtro sino un permiso: buscar un número no amplía lo visible y un alcance sin
 * permiso responde 403 aunque se llame la API directamente.
 */
export const getPanelTransactionsApi = async (
    token: string,
    query: PanelTransactionsQuery,
): Promise<PanelTransactionsApiResult> => {
    const params = new URLSearchParams({
        country: 'MX',
        page: String(query.page),
        page_size: String(query.pageSize),
        scope: query.scope,
    });

    if (query.transactionId !== null) {
        // Búsqueda directa: el número manda. Los demás filtros ni se mandan —el
        // servidor los ignoraría igual— para que el request diga exactamente lo
        // que se está pidiendo y el eco de `filters` sea comparable.
        params.set('transaction_id', String(query.transactionId));
    } else {
        const yearMonth = (query.yearMonth ?? '').trim();
        if (yearMonth !== '') {
            params.set('year_month', yearMonth);
        }

        const customerEmail = query.customerEmail.trim();
        if (customerEmail !== '') {
            params.set('customer_email', customerEmail);
        }
        // Truthiness y no `!== ''`: un filtro ausente en el estado llega como
        // `undefined` y `URLSearchParams` lo escribiría como el texto
        // "undefined", que el backend rechaza con 400 en lugar de leerlo como
        // "sin filtrar".
        if (query.paymentStatus) {
            params.set('payment_status', query.paymentStatus);
        }
        if (query.paymentMethod) {
            params.set('payment_method', query.paymentMethod);
        }
    }

    const url = `${BASE_URL}commercial_panel/transactions?${params.toString()}`;
    const res = await fetch(url, { method: 'GET', headers: headers(token) });

    // El envelope trae el mensaje de negocio también en los errores (por
    // ejemplo el 403 de alcance no habilitado), así que se lee siempre.
    const json = (await res.json().catch(() => null)) as TransactionsEnvelope | null;

    if (!res.ok || json?.success === false) {
        throw new Error(json?.errors?.message || `HTTP ${res.status}: ${FALLBACK_ERROR}`);
    }

    const data = json?.data;
    return {
        items: Array.isArray(data?.items) ? data.items : [],
        total: data?.total ?? 0,
        page: data?.page ?? query.page,
        pageSize: data?.page_size ?? query.pageSize,
        scope: data?.scope ?? query.scope,
        historyAvailableFrom: data?.history_available_from ?? null,
        summary: normalizeSummary(data?.summary),
        query,
    };
};
